/**
 * The rail both applications wear, from `design/new design /legacy`.
 *
 * The chat's rail and the back office's rail are the same object in the design's own source: a
 * 72px logo header, a scrolling middle, and a card at the foot carrying the accuracy disclaimer,
 * the confidentiality marking, the language toggle and sign-out. Only the middle differs — the
 * assistant puts the employee's conversations there, the back office puts its navigation.
 *
 * So the chrome lives here and the middle is a slot. Two copies of this would have drifted within
 * a release: the card at the foot is the one place a bank's confidentiality marking appears on
 * every screen of both applications, and it has to say the same thing in both.
 *
 * `hidden lg:flex` is the caller's business, not this component's — the chat and the back office
 * both hide it below `lg` and render it again inside a drawer, and a component that hid itself
 * could not be put in a drawer.
 */

import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSession } from "../auth/SessionProvider";
import { LanguageToggle } from "./LanguageToggle";
import { SignOutIcon } from "../ui/icons";
import { cx } from "../ui/cx";

import logoUrl from "../assets/nab-logo.png";

export interface SideRailProps {
  /** Conversation history, or navigation. The only part that differs between the two apps. */
  children: ReactNode;
  className?: string | undefined;
}

export function SideRail({ children, className }: SideRailProps): JSX.Element {
  const { t } = useTranslation();
  const { t: auth } = useTranslation("auth");
  const { signOut } = useSession();

  return (
    <div
      className={cx(
        "flex h-full w-[260px] shrink-0 flex-col border-e border-borderStrong bg-surfaceSunken",
        className,
      )}
    >
      {/* 72px, so the logo sits level with the page title in the column beside it. */}
      <div className="flex h-[72px] shrink-0 items-center justify-start border-b border-borderStrong px-4">
        <img
          src={logoUrl}
          alt={t("brand.bank")}
          className="h-10 w-auto max-w-[210px] object-contain"
        />
      </div>

      {children}

      <div className="mt-auto p-4">
        <div className="flex flex-col overflow-hidden rounded-[14px] border border-borderStrong bg-surfaceRaised shadow-sm">
          <div className="flex flex-col gap-2 p-3">
            <p className="text-label-sm font-medium leading-snug text-textPrimary">
              {t("disclaimer.accuracy")}
            </p>
            <div className="flex flex-col gap-1 border-t border-surfaceSunken pt-2 text-[9.5px] font-bold uppercase tracking-wider text-textMuted">
              <span>{t("disclaimer.confidential")}</span>
              <span>{t("disclaimer.poweredBy")}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-borderStrong bg-surface px-3 py-2">
            <LanguageToggle compact />
            <button
              type="button"
              onClick={() => void signOut()}
              title={auth("session.signOut")}
              aria-label={auth("session.signOut")}
              className="flex h-6 w-6 items-center justify-center rounded-md border border-borderStrong bg-surfaceRaised text-textMuted transition-all hover:border-statusDanger hover:bg-statusDangerSubtle hover:text-statusDanger"
            >
              <SignOutIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
